import axios, {
  type AxiosError,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import Swal from "sweetalert2";
import router from "@/router";
import { resolvePortalApiBaseUrl } from "@/constants/constants";
import {
  createPortalHybridSessionKeys,
  decryptAES,
  encryptAES,
  encryptPayload,
  getRsaPublicKey,
} from "./crypto";
import _ from "lodash";

/** Set `VITE_DEBUG_API_RESPONSES=true` to log in prod builds; in dev, logging is on unless this is `"false"`. */
function shouldLogApiResponses(): boolean {
  const v = import.meta.env.VITE_DEBUG_API_RESPONSES;
  if (v === "false") return false;
  if (v === "true") return true;
  return Boolean(import.meta.env.DEV);
}

function logApiResponseDebug(response: AxiosResponse): void {
  if (!shouldLogApiResponses()) return;
  const cfg = response.config;
  const path = `${cfg.baseURL ?? ""}${cfg.url ?? ""}`;
  // console.log("[API response]", cfg.method?.toUpperCase() ?? "GET", path, response.status, response.data);
}

function logApiErrorDebug(error: AxiosError): void {
  if (!shouldLogApiResponses()) return;
  const cfg = error.config;
  const path = cfg ? `${cfg.baseURL ?? ""}${cfg.url ?? ""}` : "";
  const status = error.response?.status;
  const data = error.response?.data;
  // console.log("[API error]", cfg?.method?.toUpperCase() ?? "?", path, status ?? error.message, data ?? error.message);
}

const service = axios.create({
  baseURL: "/api",
  timeout: 60000,
});

let offlineNoticePending = false;

function showOfflineNotice() {
  if (offlineNoticePending) return;
  offlineNoticePending = true;
  void Swal.fire({
    icon: "warning",
    title: "No internet connection",
    text: "Please check your network connection and try again.",
    confirmButtonText: "Okay",
    customClass: {
      title: "sweetAlertTitle",
      confirmButton: "sweetAlertConfrimButton",
    },
  }).finally(() => {
    offlineNoticePending = false;
  });
}

function isLikelyOfflineNetworkError(error: AxiosError): boolean {
  if (error.code === "ERR_CANCELED") return false;
  if (error.response) return false;
  if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
    return true;
  }
  if (error.code === "ECONNABORTED") return true;
  return false;
}

function applyDefaultTimeoutIfMissing(config: InternalAxiosRequestConfig) {
  const t = config.timeout;
  if (t !== undefined && t !== null && t !== 0) return;
  const isFormData =
    typeof FormData !== "undefined" && config.data instanceof FormData;
  config.timeout = isFormData ? 120000 : 30000;
}

/** Runs first: default timeouts when unset, block when browser reports offline. */
service.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  applyDefaultTimeoutIfMissing(config);

  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    showOfflineNotice();
    return Promise.reject(
      new axios.AxiosError("No internet connection", "ERR_OFFLINE", config),
    );
  }
  return config;
});

/**
 * Hybrid encrypt request bodies when true. Set via {@link initPortalEncryptionSettings} (env {@code VITE_PORTAL_ENCRYPTION_ENABLED}).
 */
let portalHybridEncryptionEnabled = true;

/** RSA key endpoints — do not run preload / body crypto on these URLs. */
const shouldSkipEncryption = (url: string) =>
  url.includes("/get-key") ||
  url.includes("/encrypt/public-key") ||
  url.includes("/dropOff");

/** Align with {@link resolvePortalApiBaseUrl} so get-key and POST hit the same backend (same {@code t_temp_rsa_key} row). */
service.interceptors.request.use((config) => {
  config.baseURL = resolvePortalApiBaseUrl();
  return config;
});

/**
 * Every portal request prefetches RSA public key material (GET /encrypt/get-key) so each round-trip
 * uses a fresh server key id. Stored on {@code config._portalRsaPreload} for the encryption step.
 */
service.interceptors.request.use(async (config) => {
  const url = config.url ?? "";
  if (!portalHybridEncryptionEnabled) {
    return config;
  }
  if (shouldSkipEncryption(url)) {
    return config;
  }
  try {
    (
      config as {
        _portalRsaPreload?: Awaited<ReturnType<typeof getRsaPublicKey>>;
      }
    )._portalRsaPreload = await getRsaPublicKey();
  } catch (e) {
    console.warn("[portal] get-key prefetch failed", e);
  }
  return config;
});

/** Deep-clone JSON-serializable payload so Vue reactive/Proxy objects encrypt the same as plain objects. */
function cloneJsonBody<T>(data: T): T {
  return JSON.parse(JSON.stringify(data)) as T;
}

/**
 * Call once after startup (e.g. in {@code main.ts}). Uses {@code VITE_PORTAL_ENCRYPTION_ENABLED}: "false" disables hybrid encrypt/decrypt on the portal.
 */
export function initPortalEncryptionSettings(): boolean {
  const env = import.meta.env.VITE_PORTAL_ENCRYPTION_ENABLED;
  if (env === "false") portalHybridEncryptionEnabled = false;
  else if (env === "true") portalHybridEncryptionEnabled = true;
  else portalHybridEncryptionEnabled = true;
  return portalHybridEncryptionEnabled;
}

export function isPortalHybridEncryptionEnabled(): boolean {
  return portalHybridEncryptionEnabled;
}

/**
 * For multipart: pass {@code portalHybridMultipartInner} on the request config; the interceptor calls
 * {@code get-key} (via preload) + wraps AES and appends {@code encryptedRequest}.
 */
export type PortalHybridMultipartAxiosConfig = {
  portalHybridMultipartInner?: Record<string, unknown>;
};

/**
 * POST/PUT/PATCH JSON that must stay plain (no hybrid encrypt). Match substring on full request URL.
 * Extend when a downstream API does not use {@code EncryptedRequest}.
 */
const PLAIN_JSON_URL_SUBSTRINGS: string[] = [];

function shouldSendPlainJson(url: string): boolean {
  return PLAIN_JSON_URL_SUBSTRINGS.some((s) => url.includes(s));
}

/**
 * Portal hybrid request — keep in sync with backend {@code PortalRequestDecryptFilter}:
 *
 * - Callers send JSON such as {@code { requestModel: { idNo, email, ... }, webformId? }}.
 * - AES plaintext is **only** the inner {@code requestModel} object (no {@code "requestModel"} key in the encrypted JSON).
 * - Wire body: {@code { encryptedRequest: "<base64>", ...otherTopLevelKeys }} plus headers
 *   {@code X-RSA-KEY-ID} and {@code X-ENCRYPTED-AES-KEY}.
 * - Server decrypts to the inner JSON, then wraps it again as {@code { requestModel: { ... } }} for Spring binding.
 */
function normalizeBodyForEncryption(
  raw: unknown,
): { plain: Record<string, unknown>; payload: object } | null {
  if (raw == null || raw === "") {
    return { plain: { requestModel: {} }, payload: {} };
  }
  if (raw instanceof FormData || Array.isArray(raw)) {
    return null;
  }
  if (typeof raw !== "object") {
    return null;
  }
  let asObject: Record<string, unknown>;
  try {
    asObject = cloneJsonBody(raw) as Record<string, unknown>;
  } catch {
    return null;
  }
  if (!_.isPlainObject(asObject)) {
    return null;
  }
  if ("requestModel" in asObject) {
    return {
      plain: asObject,
      payload: (asObject.requestModel ?? {}) as object,
    };
  }
  return {
    plain: { requestModel: asObject },
    payload: asObject as object,
  };
}

service.interceptors.request.use(async (config) => {
  const url = config.url ?? "";
  if (shouldSkipEncryption(url)) return config;

  const method = (config.method ?? "get").toLowerCase();
  if (!["post", "put", "patch"].includes(method)) {
    return config;
  }

  if (config.data instanceof FormData) {
    const inner = (config as PortalHybridMultipartAxiosConfig)
      .portalHybridMultipartInner;
    if (
      portalHybridEncryptionEnabled &&
      inner != null &&
      !shouldSkipEncryption(url) &&
      !shouldSendPlainJson(url)
    ) {
      delete (config as PortalHybridMultipartAxiosConfig)
        .portalHybridMultipartInner;
      const preloaded = (
        config as {
          _portalRsaPreload?: Awaited<ReturnType<typeof getRsaPublicKey>>;
        }
      )._portalRsaPreload;
      const { key, keyId, rawAesKey } =
        await createPortalHybridSessionKeys(preloaded);
      const data = encryptAES(inner, rawAesKey);
      config.data.append("encryptedRequest", data);
      config.headers = config.headers ?? {};
      (config.headers as Record<string, string>)["X-RSA-KEY-ID"] = keyId;
      (config.headers as Record<string, string>)["X-ENCRYPTED-AES-KEY"] = key;
      (config as any)._tempAesKey = rawAesKey;
    }
    return config;
  }

  if (!portalHybridEncryptionEnabled) {
    return config;
  }

  if (shouldSendPlainJson(url)) {
    return config;
  }

  const normalized = normalizeBodyForEncryption(config.data);
  if (normalized == null) {
    return config;
  }

  const { plain, payload } = normalized;
  const preloaded = (
    config as {
      _portalRsaPreload?: Awaited<ReturnType<typeof getRsaPublicKey>>;
    }
  )._portalRsaPreload;
  const { data, key, keyId, rawAesKey } = await encryptPayload(
    payload,
    preloaded,
  );

  // Do not send plain requestModel over the wire; backend reconstructs it from decrypted payload.
  const { requestModel: _unusedPlain, ...restTopLevel } = plain;
  config.data = {
    ...restTopLevel,
    encryptedRequest: data,
  };
  config.headers["X-RSA-KEY-ID"] = keyId;
  config.headers["X-ENCRYPTED-AES-KEY"] = key;
  (config as any)._tempAesKey = rawAesKey;

  return config;
});

service.interceptors.response.use(
  (response: AxiosResponse) => {
    const aesKey = (response.config as any)._tempAesKey;
    const resData = response.data;

    if (!portalHybridEncryptionEnabled) {
      logApiResponseDebug(response);
      return response;
    }

    if (resData?.responseModel?.encryptedResponse && aesKey) {
      const decryptedText = decryptAES(
        resData.responseModel.encryptedResponse,
        aesKey,
      );

      if (decryptedText) {
        try {
          const decryptedJSON = JSON.parse(decryptedText);
          if (
            decryptedJSON &&
            Object.prototype.hasOwnProperty.call(decryptedJSON, "responseModel")
          ) {
            response.data.responseModel = decryptedJSON.responseModel;
          } else {
            // Some endpoints (e.g. editValidate) return their value at the top
            // level instead of inside `responseModel`. Map the whole decrypted
            // payload so those fields (e.g. `response`) aren't dropped.
            response.data = decryptedJSON;
          }
        } catch (e) {
          console.error("解密後 JSON 解析失敗", e);
        }
      }
    }

    const finalModel = response.data?.responseModel;
    if (finalModel && finalModel.expiredApplication === true) {
      handleExpiredRedirect(finalModel);
    }

    logApiResponseDebug(response);
    return response;
  },
  (error: AxiosError) => {
    const cfg = error.config as { _tempAesKey?: string } | undefined;
    const aesKey = cfg?._tempAesKey;
    const resData = error.response?.data as
      | { responseModel?: { encryptedResponse?: string } }
      | undefined;

    if (
      portalHybridEncryptionEnabled &&
      resData?.responseModel?.encryptedResponse &&
      aesKey
    ) {
      const decryptedText = decryptAES(
        resData.responseModel.encryptedResponse,
        aesKey,
      );
      if (decryptedText && error.response) {
        try {
          const decryptedJSON = JSON.parse(decryptedText) as {
            responseModel?: unknown;
          };
          (error.response.data as { responseModel: unknown }).responseModel =
            decryptedJSON.responseModel;
        } catch (e) {
          console.error("錯誤回應解密後 JSON 解析失敗", e);
        }
      }
    }

    if (isLikelyOfflineNetworkError(error)) {
      showOfflineNotice();
    }

    logApiErrorDebug(error);
    return Promise.reject(error);
  },
);

const handleExpiredRedirect = (responseModel: any) => {
  const { date, time } = responseModel.submissionDateTime.dateTime;

  const localDate = new Date(
    date.year,
    date.month - 1,
    date.day,
    time.hour,
    time.minute,
    time.second,
  );

  const dateFormatter = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const timeFormatter = new Intl.DateTimeFormat("en-GB", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  router.push({
    name: "Acknowledgement",
    query: {
      status: "applicationExpired",
      cardName: responseModel.cardName,
      date: dateFormatter.format(localDate),
      time: timeFormatter.format(localDate),
    },
  });
};

export default service;
